// Cost Tracking Agent - Real-Time Budget Management
// frontend/src/agents/specialized/CostTrackingAgent.ts

import { BaseAgent } from '../core/BaseAgent';
import { AgentCommunicationHub } from '../core/AgentCommunicationHub';

export interface CostItem {
    id: string;
    name: string;
    cost: number;
    category: 'part' | 'optimization' | 'evolution' | 'labor' | 'other';
    timestamp: Date;
    approved: boolean;
    source: string;  // Which agent added this cost
}

export interface CostWarning {
    level: 'info' | 'warning' | 'critical' | 'blocked';
    message: string;
    currentCost: number;
    budget: number;
    percentUsed: number;
    recommendation?: string;
}

export interface CostBreakdown {
    parts: number;
    optimizations: number;
    evolutions: number;
    labor: number;
    other: number;
    total: number;
}

export class CostTrackingAgent extends BaseAgent {
    budget: number;
    targetCost: number;  // Ideal cost (e.g., 80% of budget)
    currentCost: number = 0;
    costHistory: CostItem[] = [];
    warnings: CostWarning[] = [];

    // Thresholds
    warningThreshold: number = 0.90;  // Warn at 90%
    criticalThreshold: number = 1.00;  // Block at 100%

    constructor(budget: number, commHub: AgentCommunicationHub) {
        super('CostTrackingAgent', commHub);
        this.budget = budget;
        this.targetCost = budget * 0.80;  // Aim for 80% of budget
    }

    /**
     * Add a cost item to tracking
     */
    async addCost(item: Omit<CostItem, 'timestamp' | 'approved'>): Promise<{ approved: boolean; reason?: string }> {
        const newCost = this.currentCost + item.cost;

        // Check if would exceed budget
        if (newCost > this.budget) {
            const warning: CostWarning = {
                level: 'blocked',
                message: `Cannot add ${item.name} ($${item.cost.toFixed(2)}). Would exceed budget!`,
                currentCost: this.currentCost,
                budget: this.budget,
                percentUsed: (newCost / this.budget) * 100,
                recommendation: 'Reject this cost or find cost reductions elsewhere'
            };

            this.warnings.push(warning);

            this.broadcast({
                eventType: 'cost-blocked',
                warning,
                item
            });

            return {
                approved: false,
                reason: warning.message
            };
        }

        // Add cost
        const costItem: CostItem = {
            ...item,
            timestamp: new Date(),
            approved: true
        };

        this.costHistory.push(costItem);
        this.currentCost = newCost;

        // Check for warnings
        this.checkThresholds();

        // Broadcast update
        this.broadcast({
            eventType: 'cost-updated',
            current: this.currentCost,
            budget: this.budget,
            remaining: this.budget - this.currentCost,
            percentUsed: (this.currentCost / this.budget) * 100,
            item: costItem
        });

        console.log(`[CostTrackingAgent] Added: ${item.name} $${item.cost.toFixed(2)} | Total: $${this.currentCost.toFixed(2)} / $${this.budget.toFixed(2)} (${((this.currentCost / this.budget) * 100).toFixed(1)}%)`);

        return { approved: true };
    }

    /**
     * Check if item can be added without exceeding budget
     */
    canAfford(cost: number): boolean {
        return (this.currentCost + cost) <= this.budget;
    }

    /**
     * Get remaining budget
     */
    getRemainingBudget(): number {
        return this.budget - this.currentCost;
    }

    /**
     * Get cost breakdown by category
     */
    getBreakdown(): CostBreakdown {
        const breakdown: CostBreakdown = {
            parts: 0,
            optimizations: 0,
            evolutions: 0,
            labor: 0,
            other: 0,
            total: this.currentCost
        };

        for (const item of this.costHistory) {
            if (item.approved) {
                breakdown[item.category] += item.cost;
            }
        }

        return breakdown;
    }

    /**
     * Check budget thresholds and issue warnings
     */
    private checkThresholds(): void {
        const percentUsed = this.currentCost / this.budget;

        if (percentUsed >= this.criticalThreshold) {
            const warning: CostWarning = {
                level: 'critical',
                message: `CRITICAL: Budget at ${(percentUsed * 100).toFixed(1)}%! At or over budget.`,
                currentCost: this.currentCost,
                budget: this.budget,
                percentUsed: percentUsed * 100,
                recommendation: 'STOP adding costs. Review and reduce.'
            };

            this.warnings.push(warning);

            this.broadcast({
                eventType: 'cost-critical',
                warning
            });

            console.warn(`[CostTrackingAgent] 🔴 CRITICAL: $${this.currentCost.toFixed(2)} / $${this.budget.toFixed(2)}`);
        }
        else if (percentUsed >= this.warningThreshold) {
            const warning: CostWarning = {
                level: 'warning',
                message: `WARNING: Budget at ${(percentUsed * 100).toFixed(1)}%. Approaching limit.`,
                currentCost: this.currentCost,
                budget: this.budget,
                percentUsed: percentUsed * 100,
                recommendation: 'Limit future cost additions. Consider cheaper alternatives.'
            };

            this.warnings.push(warning);

            this.broadcast({
                eventType: 'cost-warning',
                warning
            });

            console.warn(`[CostTrackingAgent] 🟡 WARNING: $${this.currentCost.toFixed(2)} / $${this.budget.toFixed(2)}`);
        }
        else if (this.currentCost > this.targetCost) {
            const warning: CostWarning = {
                level: 'info',
                message: `INFO: Cost above target (${((this.currentCost / this.targetCost) * 100).toFixed(1)}% of ideal).`,
                currentCost: this.currentCost,
                budget: this.budget,
                percentUsed: percentUsed * 100,
                recommendation: 'Still within budget, but watch costs closely.'
            };

            this.warnings.push(warning);

            console.log(`[CostTrackingAgent] ℹ️ INFO: $${this.currentCost.toFixed(2)} / $${this.targetCost.toFixed(2)} target`);
        }
    }

    /**
     * Propose a cost reduction plan to get back within target
     */
    async proposeCostReductions(): Promise<Array<{ item: string; savings: number; impact: string }>> {
        const overage = this.currentCost - this.targetCost;

        if (overage <= 0) {
            return [];  // Already within target
        }

        console.log(`[CostTrackingAgent] Need to save $${overage.toFixed(2)} to reach target`);

        // Sort cost items by cost/benefit ratio
        const sortedItems = [...this.costHistory]
            .filter(item => item.approved && item.category !== 'labor')  // Can't remove labor
            .sort((a, b) => b.cost - a.cost);  // Highest cost first

        const reductions: Array<{ item: string; savings: number; impact: string }> = [];

        for (const item of sortedItems) {
            // Ask optimization agent about impact of removing this
            const impact = await this.ask('OptimizationAgent', {
                question: 'What is impact of removing this cost item?',
                item: item
            });

            reductions.push({
                item: item.name,
                savings: item.cost,
                impact: impact.description || 'Unknown impact'
            });

            // If we've found enough savings, stop
            const totalSavings = reductions.reduce((sum, r) => sum + r.savings, 0);
            if (totalSavings >= overage) {
                break;
            }
        }

        return reductions;
    }

    /**
     * Reset cost tracking (for new epoch)
     */
    reset(): void {
        this.currentCost = 0;
        this.costHistory = [];
        this.warnings = [];

        this.broadcast({
            eventType: 'cost-reset'
        });

        console.log(`[CostTrackingAgent] Reset. Budget: $${this.budget.toFixed(2)}`);
    }

    /**
     * Update budget (e.g., if requirements change)
     */
    setBudget(newBudget: number): void {
        this.budget = newBudget;
        this.targetCost = newBudget * 0.80;

        // Re-check thresholds
        this.checkThresholds();

        this.broadcast({
            eventType: 'budget-updated',
            budget: this.budget,
            target: this.targetCost
        });

        console.log(`[CostTrackingAgent] Budget updated to $${this.budget.toFixed(2)}`);
    }

    /**
     * Get current status summary
     */
    getStatus(): {
        budget: number;
        targetCost: number;
        currentCost: number;
        remaining: number;
        percentUsed: number;
        status: 'good' | 'warning' | 'critical' | 'over';
        breakdown: CostBreakdown;
        recentWarnings: CostWarning[];
    } {
        const percentUsed = (this.currentCost / this.budget) * 100;

        let status: 'good' | 'warning' | 'critical' | 'over' = 'good';
        if (percentUsed >= 100) {
            status = 'over';
        } else if (percentUsed >= 90) {
            status = 'critical';
        } else if (this.currentCost > this.targetCost) {
            status = 'warning';
        }

        return {
            budget: this.budget,
            targetCost: this.targetCost,
            currentCost: this.currentCost,
            remaining: this.budget - this.currentCost,
            percentUsed,
            status,
            breakdown: this.getBreakdown(),
            recentWarnings: this.warnings.slice(-5)  // Last 5 warnings
        };
    }
}
